import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker - use jsdelivr CDN as alternative
// Force set the worker source to avoid caching issues
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
}

/**
 * Convert the first page of a PDF to a PNG image data URL
 * @param pdfUrl - URL of the PDF file
 * @returns Data URL of the rendered PDF page as PNG
 */
export async function convertPdfToImage(pdfUrl: string): Promise<string> {
  try {
    // Load the PDF document with CORS settings
    const loadingTask = pdfjsLib.getDocument({
      url: pdfUrl,
      withCredentials: false,
      isEvalSupported: false,
    });
    const pdf = await loadingTask.promise;

    // Get the first page
    const page = await pdf.getPage(1);

    // Set scale for good quality (2.0 = 2x resolution)
    const scale = 2.0;
    const viewport = page.getViewport({ scale });

    // Create a canvas to render the PDF page
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Could not get canvas context');
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Render the PDF page to the canvas
    const renderContext: any = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;

    // Convert canvas to data URL (PNG format)
    const imageDataUrl = canvas.toDataURL('image/png', 0.95);

    return imageDataUrl;
  } catch (error) {
    console.error('Error converting PDF to image:', error);
    throw new Error('Failed to convert PDF to image');
  }
}

/**
 * Check if a file is a PDF based on URL or MIME type
 * @param fileUrl - URL of the file
 * @param mimeType - MIME type of the file (optional)
 * @returns true if the file is a PDF
 */
export function isPdfFile(fileUrl: string, mimeType?: string): boolean {
  return (
    mimeType?.toLowerCase().includes('pdf') ||
    fileUrl.toLowerCase().endsWith('.pdf')
  );
}
